import {useState, useEffect} from 'react'
import {Box} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import dayjs from 'dayjs'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import SelectNew from 'src/components-new/inputs/SelectNew'
import DatePickerNew from 'src/components-new/inputs/DatePickerNew'
import TimePickerNew from 'src/components-new/inputs/TimePickerNew'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import CheckboxGroupNew from 'src/components-new/inputs/CheckboxGroupNew'
import RadioGroupNew from 'src/components-new/inputs/RadioGroupNew'
import {useAppSelector} from 'src/hooks'
import {
  CalendarEvent,
  WarningUnit,
  warningOptions,
  siteOptions,
  computeWarning,
  mergeDateAndTime,
} from './agendaUtils'
import ParticipantLookupDialogNew from './ParticipantLookupDialogNew'

interface EventDialogNewProps {
  open: boolean
  event?: CalendarEvent
  eventsTypes?: Record<string, any>[]
  patientDetailUser?: Record<string, any>
  onClose: () => void
  onSave: (values: CalendarEvent) => void
}

/**
 * warningOptions values are numbers; SelectNew requires string values.
 * Convert here so the selector works correctly.
 */
const warningOptionStrings = warningOptions.map(opt => ({
  label: opt.label,
  value: String(opt.value),
}))

/**
 * siteOptions values are already strings — pass through directly.
 */
const siteOptionStrings = siteOptions.map(opt => ({
  label: opt.label,
  value: opt.value,
}))

const CHANNEL_OPTIONS = [
  {label: 'SMS', value: 'sms'},
  {label: 'E-mail', value: 'e-mail'},
  {label: 'Push', value: 'push'},
]

const SHARE_OPTIONS = [
  {label: 'Sì', value: 'true'},
  {label: 'No', value: 'false'},
]

export default function EventDialogNew({
  open,
  event,
  eventsTypes,
  patientDetailUser,
  onClose,
  onSave,
}: EventDialogNewProps): JSX.Element {
  const {participantUserGroup} = useAppSelector(
    state => state.pspConfigReducer.data.agendaConfigurations
  )
  const {_id: userId, userGroup} = useAppSelector(state => state.userReducer.data)

  const [eventTypeId, setEventTypeId] = useState<string>('')
  const [site, setSite] = useState<string>('')
  const [startDay, setStartDay] = useState<string | null>(null)
  const [startTime, setStartTime] = useState<string | null>(null)
  const [endTime, setEndTime] = useState<string | null>(null)
  const [warningValue, setWarningValue] = useState<string>('')
  const [warningUnit, setWarningUnit] = useState<string>('')
  const [channels, setChannels] = useState<string[]>([])
  const [currentUser, setCurrentUser] = useState<Record<string, any> | undefined>()
  const [userDisplayName, setUserDisplayName] = useState<string>('')
  const [shareWithPatient, setShareWithPatient] = useState<string>('')
  const [submitted, setSubmitted] = useState(false)
  const [title, setTitle] = useState<string>('')
  const [lookupOpen, setLookupOpen] = useState(false)

  useEffect(() => {
    const foundType = eventsTypes?.find(et => et.name === event?.title)
    setEventTypeId(foundType?._id ?? '')
    setTitle(event?.title ?? '')

    setSite(event?.site ?? '')
    setStartDay(event?.startDate ?? null)
    setStartTime(event?.startDate ?? null)
    setEndTime(event?.endDate ?? null)
    setChannels(event?.channels ?? [])
    setShareWithPatient(
      event?.shareWithPatient === undefined ? '' : String(event.shareWithPatient)
    )

    const {value: wVal, unit: wUnit} = computeWarning(event?.reminderMilliseconds)
    setWarningValue(wVal !== undefined ? String(wVal) : '')
    setWarningUnit(wUnit !== undefined ? String(wUnit) : '')

    if (patientDetailUser) {
      setCurrentUser(patientDetailUser)
      setUserDisplayName(
        patientDetailUser.firstName && patientDetailUser.lastName
          ? `${patientDetailUser.firstName} ${patientDetailUser.lastName}`
          : ''
      )
    } else {
      const participant =
        participantUserGroup === 'DOCTOR'
          ? event?.participants?.find(p => p.type === 'doctors' && p._id !== userId)
          : event?.participants?.find(p => p.type === 'patients')
      setCurrentUser(participant)
      if (participant?.firstName && participant?.lastName) {
        setUserDisplayName(`${participant.firstName} ${participant.lastName}`)
      } else {
        setUserDisplayName(typeof event?.user === 'string' ? event.user : '')
      }
    }
  }, [event, eventsTypes, patientDetailUser, participantUserGroup, userId])

  useEffect(() => {
    if (!open) setSubmitted(false)
  }, [open])

  const isPatientOrInsurance =
    userGroup?.toUpperCase().includes('PATIENT') ||
    userGroup?.toUpperCase().includes('INSURANCEUSER')

  const isPatientReadOnly =
    userGroup?.toUpperCase().includes('PATIENT') && event?.shareWithPatient === true

  const endTimeError =
    submitted && endTime && startTime
      ? !dayjs(endTime).isAfter(dayjs(startTime))
      : false

  const reasonOptions = (eventsTypes ?? []).map(et => ({
    label: et.name as string,
    value: et._id as string,
  }))

  const handleSubmit = () => {
    setSubmitted(true)
    if (!eventTypeId || !startDay || !startTime || !endTime) return

    const startISO = mergeDateAndTime(startDay, startTime)
    const endISO = mergeDateAndTime(startDay, endTime)
    if (!startISO || !endISO || !dayjs(endISO).isAfter(dayjs(startISO))) return

    const wValNum = warningValue ? Number(warningValue) : undefined
    const wUnitNum = warningUnit ? (Number(warningUnit) as WarningUnit) : undefined

    const body: CalendarEvent = {
      title,
      startDate: startISO,
      endDate: endISO,
      participants: event?.participants ?? [],
      site: site || undefined,
      channels,
      shareWithPatient:
        shareWithPatient === '' ? undefined : shareWithPatient === 'true',
      reminderMilliseconds:
        wValNum !== undefined && wUnitNum !== undefined
          ? wValNum * wUnitNum
          : undefined,
      warning: wValNum !== undefined && wUnitNum !== undefined
        ? { value: wValNum, unit: wUnitNum }
        : undefined,
      user: currentUser ?? (userDisplayName || undefined),
      type: 'PERSONAL_EVENT',
      participantUserGroup,
    } as any

    onSave(body)
  }

  const dialogTitle =
    participantUserGroup === 'DOCTOR' ? 'Appuntamento' : 'Evento paziente'

  return (
    <>
      <AppDialog
        open={open}
        onClose={onClose}
        title={dialogTitle}
        maxWidth="md"
        actions={
          <>
            <Button variant="secondary" onClick={onClose}>
              Annulla
            </Button>
            <Button variant="primary" onClick={handleSubmit}>
              Salva
            </Button>
          </>
        }
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
            gap: 2,
            pt: 1,
          }}
        >
          {/* Field 1 — Motivo (full width) */}
          <SelectNew
            sx={{gridColumn: '1 / -1'}}
            label="Motivo"
            required
            value={eventTypeId}
            onChange={(value) => {
              setEventTypeId(value)
              const found = eventsTypes?.find(et => et._id === value)
              setTitle(found?.name ?? '')
            }}
            options={reasonOptions}
            disabled={isPatientReadOnly}
            error={submitted && !eventTypeId}
            helperText={submitted && !eventTypeId ? 'Campo obbligatorio' : undefined}
          />

          {/* Field 2 — Dove */}
          <SelectNew
            label="Dove"
            value={site}
            onChange={setSite}
            options={siteOptionStrings}
            disabled={isPatientReadOnly}
          />

          {/* Field 3 — Data evento */}
          <DatePickerNew
            label="Data evento"
            required
            value={startDay}
            onChange={setStartDay}
            disabled={isPatientReadOnly}
            error={submitted && !startDay}
            helperText={submitted && !startDay ? 'Campo obbligatorio' : undefined}
          />

          {/* Field 4 — Dalle */}
          <TimePickerNew
            label="Dalle"
            required
            value={startTime}
            onChange={setStartTime}
            disabled={isPatientReadOnly}
            error={submitted && !startTime}
            helperText={submitted && !startTime ? 'Campo obbligatorio' : undefined}
          />

          {/* Field 5 — Alle */}
          <TimePickerNew
            label="Alle"
            required
            value={endTime}
            onChange={setEndTime}
            disabled={isPatientReadOnly}
            error={(submitted && !endTime) || Boolean(endTimeError)}
            helperText={
              submitted && !endTime
                ? 'Campo obbligatorio'
                : endTimeError
                  ? "L'orario di fine deve essere successivo all'orario di inizio"
                  : undefined
            }
          />

          {/* Field 6 — Avvisami (two sub-fields side by side) */}
          <Box sx={{display: 'flex', gap: 1}}>
            <TextFieldNew
              label="Avvisami"
              value={warningValue}
              onChange={(val) => {
                const numeric = val.replace(/[^0-9]/g, '')
                setWarningValue(numeric)
              }}
              placeholder="0"
              sx={{flex: 1}}
            />
            <SelectNew
              label="Unità"
              value={warningUnit}
              onChange={setWarningUnit}
              options={warningOptionStrings}
              sx={{flex: 1}}
            />
          </Box>

          {/* Field 7 — Metodo di avviso (full width) */}
          <CheckboxGroupNew
            sx={{gridColumn: '1 / -1'}}
            label="Metodo di avviso"
            value={channels}
            onChange={setChannels}
            options={CHANNEL_OPTIONS}
          />

          {/* Field 8 — Partecipante (conditional) */}
          {!isPatientOrInsurance && (
            <Box sx={{gridColumn: '1 / -1'}}>
              {participantUserGroup ? (
                <Box sx={{display: 'flex', gap: 1, alignItems: 'flex-end'}}>
                  <TextFieldNew
                    label={participantUserGroup === 'PATIENT' ? 'Paziente' : 'Altro partecipante'}
                    value={userDisplayName}
                    onChange={() => undefined}
                    disabled
                    required
                    error={submitted && !userDisplayName}
                    helperText={submitted && !userDisplayName ? 'Campo obbligatorio' : undefined}
                    sx={{flex: 1}}
                  />
                  {!patientDetailUser && (
                    <>
                      <Button
                        variant="ghost"
                        onClick={() => setLookupOpen(true)}
                        startIcon={<SearchIcon fontSize="small" />}
                        sx={{flexShrink: 0}}
                      >
                        Cerca
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setCurrentUser(undefined)
                          setUserDisplayName('')
                        }}
                        startIcon={<CloseIcon fontSize="small" />}
                        sx={{flexShrink: 0}}
                      >
                        Rimuovi
                      </Button>
                    </>
                  )}
                </Box>
              ) : (
                <TextFieldNew
                  label="Altro partecipante"
                  value={userDisplayName}
                  onChange={setUserDisplayName}
                />
              )}
            </Box>
          )}

          {/* Field 9 — Condividi con il paziente (conditional) */}
          {participantUserGroup === 'PATIENT' && !isPatientOrInsurance && (
            <RadioGroupNew
              sx={{gridColumn: '1 / -1'}}
              label="Condividi con il paziente"
              value={shareWithPatient}
              onChange={setShareWithPatient}
              options={SHARE_OPTIONS}
            />
          )}
        </Box>
      </AppDialog>

      {lookupOpen && (
        <ParticipantLookupDialogNew
          mode={participantUserGroup === 'DOCTOR' ? 'doctor' : 'patient'}
          onClose={() => setLookupOpen(false)}
          onRowClick={(row) => {
            setCurrentUser(row)
            setUserDisplayName(`${row.firstName} ${row.lastName}`)
            setLookupOpen(false)
          }}
        />
      )}
    </>
  )
}
